import type {
  SurveyBranchOperator,
  SurveyBranchRule,
  SurveyFieldDefinition,
} from '@engage/types';
import { Icon, ICONS, INPUT_STYLE } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';

const OPERATORS: { value: SurveyBranchOperator; label: string }[] = [
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
];

/**
 * Compound branching editor for one page. Each rule: a connector (ALL/ANY)
 * over one-or-more conditions, then a goto target (another page or END).
 *
 * Rules are evaluated top-to-bottom; first match wins; if none match the
 * respondent falls through to the next sequential page. Conditions may only
 * reference fields on this page or earlier pages (`availableFields`) — the same
 * forward-only rule the server validator enforces.
 */
export function BranchingEditor(props: {
  rules: SurveyBranchRule[];
  availableFields: SurveyFieldDefinition[];
  gotoTargets: { id: string; label: string }[];
  onAddRule: () => void;
  onRemoveRule: (ruleId: string) => void;
  onUpdateRule: (ruleId: string, patch: Partial<SurveyBranchRule>) => void;
  onAddCondition: (ruleId: string) => void;
  onRemoveCondition: (ruleId: string, index: number) => void;
  onUpdateCondition: (
    ruleId: string,
    index: number,
    patch: Partial<SurveyBranchRule['conditions'][number]>
  ) => void;
}) {
  return (
    <div style={{ fontFamily: FORM_UI_FONT }}>
      <div style={{ fontSize: 11, color: C.faint, lineHeight: 1.5, marginBottom: 10 }}>
        Rules run top to bottom. The first whose conditions match decides where the respondent goes
        next. With no matching rule they continue to the next page.
      </div>

      {props.availableFields.length === 0 && (
        <div style={{ fontSize: 11.5, color: C.warn, background: C.warnBg, borderRadius: 8, padding: '9px 11px', marginBottom: 10 }}>
          Add a question to this page (or an earlier one) before creating a branch. Conditions need a
          field to test.
        </div>
      )}

      {props.rules.map((rule, ri) => (
        <div
          key={rule.id}
          style={{ border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 12px 10px', marginBottom: 10, background: '#fff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: C.indigo, letterSpacing: 0.3 }}>RULE {ri + 1}</span>
            <button
              type="button"
              onClick={() => props.onRemoveRule(rule.id)}
              title="Remove rule"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.faint, padding: 2 }}
            >
              <Icon d={ICONS.x} size={14} color={C.faint} />
            </button>
          </div>

          {/* connector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11.5, color: C.sub }}>Match</span>
            <div style={{ display: 'flex', border: `1px solid ${C.line}`, borderRadius: 7, overflow: 'hidden' }}>
              <ConnectorBtn active={rule.connector === 'AND'} onClick={() => props.onUpdateRule(rule.id, { connector: 'AND' })}>
                ALL
              </ConnectorBtn>
              <ConnectorBtn active={rule.connector === 'OR'} onClick={() => props.onUpdateRule(rule.id, { connector: 'OR' })}>
                ANY
              </ConnectorBtn>
            </div>
            <span style={{ fontSize: 11.5, color: C.sub }}>of:</span>
          </div>

          {/* conditions */}
          {rule.conditions.map((cond, ci) => (
            <div key={ci} style={{ display: 'flex', gap: 5, marginBottom: 6, alignItems: 'center' }}>
              <select
                value={cond.field_key}
                onChange={(e) => props.onUpdateCondition(rule.id, ci, { field_key: e.target.value })}
                style={{ ...INPUT_STYLE, flex: 1.4, padding: '7px 8px' }}
              >
                <option value="">Field…</option>
                {props.availableFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
              <select
                value={cond.operator}
                onChange={(e) => props.onUpdateCondition(rule.id, ci, { operator: e.target.value as SurveyBranchOperator })}
                style={{ ...INPUT_STYLE, flex: 0.8, padding: '7px 8px' }}
              >
                {OPERATORS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                value={String(cond.value)}
                onChange={(e) => props.onUpdateCondition(rule.id, ci, { value: coerceValue(e.target.value, cond.operator) })}
                placeholder="Value"
                style={{ ...INPUT_STYLE, flex: 1, padding: '7px 8px' }}
              />
              <button
                type="button"
                onClick={() => props.onRemoveCondition(rule.id, ci)}
                disabled={rule.conditions.length <= 1}
                title="Remove condition"
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 7,
                  border: `1px solid ${C.line}`,
                  background: '#fff',
                  cursor: rule.conditions.length <= 1 ? 'default' : 'pointer',
                  opacity: rule.conditions.length <= 1 ? 0.4 : 1,
                }}
              >
                <Icon d={ICONS.x} size={11} color={C.faint} sw={2.2} />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => props.onAddCondition(rule.id)}
            style={{ ...linkBtn, marginBottom: 10 }}
          >
            + Add condition
          </button>

          {/* goto */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: `1px solid ${C.line}`, paddingTop: 9 }}>
            <span style={{ fontSize: 11.5, color: C.sub, whiteSpace: 'nowrap' }}>then go to</span>
            <select
              value={rule.goto_page_id}
              onChange={(e) => props.onUpdateRule(rule.id, { goto_page_id: e.target.value })}
              style={{ ...INPUT_STYLE, flex: 1, padding: '7px 8px' }}
            >
              {props.gotoTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      ))}

      <button type="button" onClick={props.onAddRule} disabled={props.availableFields.length === 0} style={addBtn(props.availableFields.length === 0)}>
        <Icon d={ICONS.plus} size={12} color={C.tealDark} sw={2.4} /> Add branching rule
      </button>
    </div>
  );
}

function ConnectorBtn(props: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        border: 'none',
        background: props.active ? C.indigo : '#fff',
        color: props.active ? '#fff' : C.sub,
        fontSize: 11,
        fontWeight: 700,
        padding: '6px 12px',
        cursor: 'pointer',
        fontFamily: FORM_UI_FONT,
      }}
    >
      {props.children}
    </button>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: C.tealDark,
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: FORM_UI_FONT,
  padding: '2px 0',
};

function addBtn(disabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${disabled ? C.line : C.teal}`,
    background: '#fff',
    color: disabled ? C.faint : C.tealDark,
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? 'default' : 'pointer',
    fontFamily: FORM_UI_FONT,
  };
}

/** gte/lte compare numerically when the entered value is numeric. */
function coerceValue(raw: string, operator: SurveyBranchOperator): string | number {
  if ((operator === 'gte' || operator === 'lte') && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return raw;
}
